import React, { useEffect, useState } from 'react';
import { FileIcon, ExternalLink, ImageOff } from 'lucide-react';
import { getReimbursementFileUrl } from '../../../Services/APIs/reimbursement';

const isImage = (url) => /\.(jpe?g|png)$/i.test(String(url || '').split('?')[0]);

// A file already stored on the member's Reimbursement. The bucket is private, so it is shown
// through a short-lived signed link fetched from the API (never the raw stored URL). `claimId`
// picks one proof's file (the API defaults to the current proof). variant 'thumb' is a square tile
// that opens the file in a new tab.
const StoredFile = ({ reimbursementId, claimId, field, index = 0, storedUrl, label, variant = 'full' }) => {
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);
    const image = isImage(storedUrl);
    const thumb = variant === 'thumb';

    useEffect(() => {
        let cancelled = false;
        setUrl(null);
        setFailed(false);
        getReimbursementFileUrl(reimbursementId, field, index, claimId)
            .then((response) => {
                if (cancelled) return;
                const signed = response.data?.data?.url || null;
                setUrl(signed);
                if (!signed) setFailed(true);
            })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [reimbursementId, claimId, field, index]);

    if (thumb) {
        const tile = 'aspect-square w-full rounded-xl border border-portal-border bg-white overflow-hidden flex items-center justify-center';
        if (failed) {
            return (
                <div className={`${tile} flex-col gap-1 text-portal-sub`} role="img" aria-label={`${label}: could not load`}>
                    <ImageOff className="w-5 h-5" aria-hidden="true" />
                    <span className="text-[11px]">Not available</span>
                </div>
            );
        }
        if (!url) return <div className={`${tile} bg-portal-bg animate-pulse`} aria-label={`Loading ${label}`} />;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className={`${tile} hover:border-portal-purple focus:outline-none focus:ring-2 focus:ring-portal-purple`} aria-label={`Open ${label}`}>
                {image ? (
                    <img src={url} alt={label} className="w-full h-full object-cover" onError={() => setFailed(true)} />
                ) : (
                    <span className="flex flex-col items-center gap-1 text-portal-purple-dark">
                        <FileIcon className="w-6 h-6" aria-hidden="true" />
                        <span className="text-[11px] font-semibold">Open</span>
                    </span>
                )}
            </a>
        );
    }

    if (failed) {
        return <p className="text-xs text-portal-sub">{label}: could not load the current file.</p>;
    }
    if (!url) {
        return <div className="h-24 rounded-xl bg-portal-bg animate-pulse" aria-label={`Loading ${label}`} />;
    }
    if (image) {
        return <img src={url} alt={label} className="w-full max-h-40 object-contain rounded-xl border border-portal-border bg-white" onError={() => setFailed(true)} />;
    }
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-portal-border rounded-xl px-3 py-2 text-xs text-portal-ink bg-white hover:bg-portal-bg">
            <FileIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="truncate flex-1">Open current {label.toLowerCase()}</span>
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
        </a>
    );
};

export default StoredFile;
